<script setup lang="ts">
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import type { CurrentSubscriptionInfo, Offerings, PlanData, PlanProduct } from '@/types/billing';
import { Button, Divider, Heading, LayoutGap, Prose } from '@dabble/quillui';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import Price from '../../components/Price.vue';
import Features from './Features.vue';

const billingStore = useBillingStore();

const props = defineProps<{
  product: PlanProduct | null;
  current: CurrentSubscriptionInfo | null;
  selection: 'month' | 'year';
  productID: string;
  isTrialing?: boolean;
  recommend?: boolean;
  isLegacy?: boolean;
  offerings: Offerings | null;
}>();

const router = useRouter();

const t = (_key: string, fallback: string) => fallback;

const images: { [key: string]: string } = {
  basic: '/src/views/account/billing/images/plan-basic.svg',
  standard: '/src/views/account/billing/images/plan-standard.svg',
  premium: '/src/views/account/billing/images/plan-premium.svg',
  lifetime: '/src/views/account/billing/images/plan-premium.svg',
  free: '/src/views/account/billing/images/plan-premium.svg',
};

const oldPlanOfferings = computed(() => props.current?.offerings?.plans?.plans?.[props.current?.product?.id ?? '']);

const planData = computed<PlanData | null>(() => {
  const product = props.product;
  if (!product) return null;

  if (product['one-time']) {
    return product['one-time'];
  }
  if (product[props.selection]) {
    return product[props.selection] ?? null;
  }
  const oldOffers = oldPlanOfferings.value;
  if (oldOffers?.[props.selection]) {
    return oldOffers[props.selection] ?? null;
  }
  return oldOffers?.year ?? oldOffers?.month ?? null;
});

const isCurrentProduct = computed(() => props.current?.product?.id === props.productID);

const isCurrentPlan = computed(() => planData.value?.id === props.current?.id);

const name = computed(() => {
  if (props.current && isCurrentProduct.value) {
    return props.current.product?.name ?? 'Unnamed Plan';
  }
  return props.product?.name ?? 'Unnamed Plan';
});

const description = computed(() => {
  let desc = 'Needs a description';
  if (props.current && isCurrentProduct.value) {
    desc = props.current.product?.description || desc;
  } else if (props.product?.description) {
    desc = props.product.description;
  }
  return desc;
});

const image = computed(() => {
  if (!name.value) return null;
  const lowerName = name.value.toLowerCase();
  const key = lowerName.startsWith('free') ? 'free' : lowerName;
  return images[key] || null;
});

const buttonText = computed(() => {
  const baseText = t('checkout_select_plan', 'Select Plan');
  if (isCurrentPlan.value) {
    const suffix = props.isTrialing ? ' (Current Trial)' : ' (Current)';
    return baseText + suffix;
  }
  return baseText;
});

async function checkout() {
  await billingStore.fetchBillingOverview();
  billingStore.selectedPlan = planData.value;
  billingStore.selectedPlanId = planData.value?.id ?? null;
  billingStore.product = props.offerings?.plans?.plans?.[props.productID] as PlanProduct;
  billingStore.selection = props.selection;
  if (billingStore.selectedPlan && planData.value?.id) {
    router.push('/billing/checkout');
  } else {
    logError('Cannot checkout, plan ID is missing.');
  }
}
</script>

<template>
  <LayoutGap direction="column" align="center" size="small" class="plan-container">
    <Heading :level="3" align="center" no-margin class="plan-container__title">{{ name }}</Heading>
    <Prose variant="secondary" size="small" align="center" class="plan-description">{{ description }}</Prose>

    <Button variant="primary" fill-width :disabled="isCurrentPlan && !isTrialing" @click="checkout">
      {{ buttonText }}
    </Button>

    <LayoutGap v-if="planData" direction="row" align="center" vertical-align="center" size="none" class="price">
      <Price :plan="planData" />
    </LayoutGap>
    <Divider variant="secondary" size="small" class="plan-separator" />
    <img v-if="image" :src="image" :alt="name || 'Plan icon'" class="plan-image" />

    <Features :plan="name" />
  </LayoutGap>
</template>

<style lang="scss" scoped>
.plan-container {
  width: 100%;
  max-width: 20rem;
  padding: 1.5rem;
  border: 1px solid var(--border-secondary, #e5e7eb);
  border-radius: 0.5rem;
  background-color: var(--background-primary, #fff);
  box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06));
}

.plan-container__title {
  font-family: var(--font-family-ui-serif, ui-serif, serif);
}

.plan-description {
  height: 3rem;
  overflow: hidden;
  line-height: 1.4;
}

.price {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  min-height: 5rem;
}

.plan-separator {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}

.plan-image {
  max-width: 100px;
  height: auto;
  margin-bottom: 1.5rem;
}
</style>
